import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ConnectorSwitch from '../ConnectorSwitch';
import NiagaraGridItems from '../Resource/Organization/NiagaraGridItems';

// ─── Licensing ────────────────────────────────────────────────────────────────

export default function Licensing() {
  const { organizations, meters, niagaras } = useSelector(
    (state) => state.demand
  );
  const [devicesOrg, setDevicesOrg] = useState(null);

  const rows = useMemo(() => {
    const nonPortfolioOrgs = filter(organizations, (o) => !o.is_portfolio);
    return sortBy(
      nonPortfolioOrgs.map((org) => {
        const orgMeters = filter(meters, { org_id: org.org_id });
        const rtoIds = uniq(compact(orgMeters.map((m) => m.rto_id)));
        const niagara = find(niagaras, { org_id: org.org_id });
        return { org, rtoIds, niagara };
      }),
      (r) => r.org.name ?? ''
    );
  }, [organizations, meters, niagaras]);

  return (
    <Box sx={{ maxWidth: 700, width: '100%', px: 1, mt: 1, mb: 5 }}>
      <Card variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>RTO</TableCell>
              <TableCell align='center'>Connector Active</TableCell>
              <TableCell align='center'>Devices</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ org, rtoIds, niagara }) => (
              <TableRow key={org.org_id} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {org.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {rtoIds.length > 0 ? (
                    <Stack direction='row' spacing={0.5}>
                      {rtoIds.map((rtoId) => (
                        <Chip
                          key={rtoId}
                          size='small'
                          label={rtoId}
                          variant='outlined'
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant='caption' color='text.disabled'>
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align='center'>
                  {niagara ? (
                    <ConnectorSwitch niagara={niagara} orgName={org.name} />
                  ) : (
                    <Typography variant='caption' color='text.disabled'>
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align='center'>
                  {niagara ? (
                    <Tooltip title='Manage Niagara devices'>
                      <IconButton
                        size='small'
                        onClick={() => setDevicesOrg(org)}
                      >
                        <FontAwesomeIcon icon={['fal', 'router']} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography variant='caption' color='text.disabled'>
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ py: 1 }}
                  >
                    No organizations found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Dialog
        open={Boolean(devicesOrg)}
        onClose={() => setDevicesOrg(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>{devicesOrg?.name} — Niagara</DialogTitle>
        <DialogContent>
          {devicesOrg && <NiagaraGridItems organization={devicesOrg} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
